import type {BadgeADto, ReorderBadgesADto} from '@cohort/admin-schemas/badge';
import {Badge} from '@cohort/merchants/components/Badge';
import Button from '@cohort/merchants/components/buttons/Button';
import DraggableList from '@cohort/merchants/components/form/DraggableList';
import DraggableListItem from '@cohort/merchants/components/form/DraggableListItem';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetTitle,
} from '@cohort/merchants/components/modals/Sheet';
import BadgeVisual from '@cohort/merchants/pages/users/badges/BadgeVisual';
import BadgeRankBox from '@cohort/merchants/pages/users/badges/overview/BadgeRankBox';
import Tooltip from '@cohort/shared-frontend/components/Tooltip';
import {EyeSlash, X} from '@phosphor-icons/react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

type MoveBadgeDirection = 'up' | 'down';

type BadgeListItemProps = {
  badge: BadgeADto;
  index: number;
  moveBadge: (currentIndex: number, direction: MoveBadgeDirection) => void;
};

const BadgeListItem: React.FC<BadgeListItemProps> = ({badge, index, moveBadge}) => {
  const {t} = useTranslation('pages', {keyPrefix: 'users.badges.overview.reorderBadgesDrawer'});

  return (
    <div className="flex items-center space-x-4">
      <BadgeRankBox rank={index + 1} />
      <DraggableListItem
        onItemUp={() => moveBadge(index, 'up')}
        onItemDown={() => moveBadge(index, 'down')}
        className="w-full bg-slate-50"
        grow
      >
        <div className="flex items-center justify-between gap-x-3">
          <div className="flex space-x-3">
            <BadgeVisual imageFileKey={badge.imageFileKey} size={8} />
            <div className="flex flex-row items-center gap-3 text-sm font-medium">
              {badge.internalName}
              {badge.visibility === 'cohort' && (
                <Tooltip content={t('hiddenBadge')}>
                  <EyeSlash size={20} className="text-slate-500" />
                </Tooltip>
              )}
            </div>
          </div>
          {badge.status === 'draft' && (
            <Badge
              size="small"
              text={t('badgeStatusDraft')}
              textColor="text-yellow-800"
              backgroundColor="bg-yellow-100"
              dotColor="text-yellow-400"
            />
          )}
        </div>
      </DraggableListItem>
    </div>
  );
};

type ReorderBadgesDrawerProps = {
  badges: Array<BadgeADto>;
  onClose: () => void;
  onSave: (data: ReorderBadgesADto) => void;
};

const ReorderBadgesDrawer: React.FC<ReorderBadgesDrawerProps> = ({badges, onClose, onSave}) => {
  const [badgesList, setBadgesList] = useState(badges);
  const {t} = useTranslation('pages', {keyPrefix: 'users.badges.overview.reorderBadgesDrawer'});

  const moveBadge = (currentIndex: number, direction: MoveBadgeDirection): void => {
    const badgesCopy = [...badgesList];
    const badge = badgesCopy.splice(currentIndex, 1)[0] as BadgeADto;

    if (direction === 'up') {
      if (currentIndex === 0) {
        badgesCopy.push(badge);
      } else {
        badgesCopy.splice(currentIndex - 1, 0, badge);
      }
    } else {
      if (currentIndex === badgesList.length - 1) {
        badgesCopy.unshift(badge);
      } else {
        badgesCopy.splice(currentIndex + 1, 0, badge);
      }
    }

    setBadgesList(badgesCopy);
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="flex h-full flex-col gap-0 p-6">
        <div className="mb-6 space-y-2">
          <SheetTitle className="text-xl font-semibold">{t('title')}</SheetTitle>
          <p className="text-sm font-normal text-slate-500">{t('subtitle')}</p>
        </div>
        <div className="flex h-full flex-col justify-between">
          <div className="grow">
            <DraggableList
              handleOnReorder={badgeIds => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                setBadgesList(badgeIds.map(id => badgesList.find(badge => badge.id === id)!));
              }}
              items={badgesList.map((badge, index) => ({
                id: badge.id,
                item: (
                  <BadgeListItem key={badge.id} badge={badge} index={index} moveBadge={moveBadge} />
                ),
              }))}
            />
          </div>

          <SheetFooter>
            <div className="flex w-full items-center justify-between">
              <Button variant="secondary" onClick={onClose} data-testid="cancel">
                <X className="-ml-1 mr-2 h-5 w-5" />
                {t('buttonCancel')}
              </Button>
              <Button
                form="campaign-form-faq"
                onClick={() => onSave(badgesList.map(badge => badge.id))}
              >
                {t('buttonEdit')}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ReorderBadgesDrawer;
